import { useTranslation } from "react-i18next";
import { CircleCheck, CircleXIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/v2/spinner";
import { RequestDetailsHeader } from "@/components/RequestDetailsHeader";
import {
  RequestDetailLayout,
  RequestInfoGrid,
  approvalFlowTab,
} from "@/components/request-detail";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useApproverContext } from "@/hooks/useApproverContext";
import { useCompanyGetAssetRequestDetailsByIdQuery } from "@/services/(company)/api.assets";
import { ActionAcceptConsumableAssetReqDialog } from "./asset-requests-table/ActionAcceptConsumableAssetReqDialog";
import { ActionAcceptAssetReqDialog } from "./asset-requests-table/ActionAcceptAssetReqDialog";
import { ActionRejectAssetReqDialog } from "./asset-requests-table/ActionRejectAssetRequest";
import AssetInfo from "@/components/workflow/_components/AssetInfo";
import { PermissionGuard } from "@/components/permission-guard";
import DocumentAttachments from "@/pages/(home)/_components/DocumentAttachments";
import { AssetCategories } from "@/utils/enums";
import { buildAssetInfoItems } from "./buildAssetInfoItems";

export const AssetRequestView = () => {
  const { t } = useTranslation(["assets", "common"]);
  const navigate = useNavigate();
  const params = useParams();
  const assetRequestId = Number(params.requestId);

  const { data: assetData, isFetching: isLoading } =
    useCompanyGetAssetRequestDetailsByIdQuery(
      { requestId: assetRequestId! },
      { skip: !assetRequestId, refetchOnMountOrArgChange: true },
    );

  const assetRequest = assetData?.data?.assetRequest;
  const { isPending } = useApproverContext(undefined, assetRequest?.status);
  const attachmentUrl = assetRequest?.attachmentUrl as string | null;

  if (isLoading) return <Spinner />;

  return (
    <RequestDetailLayout
      sidebarTabs={[
        approvalFlowTab({
          approvalSteps: assetData?.data?.approvalSteps,
          triggerContent: (
            <AssetInfo
              assetTypeName={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.assetTypeName
              }
              requestType={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.requestType
              }
              description={
                assetData?.data?.approvalSteps?.startingPoint?.condition
                  ?.description
              }
              requestDate={assetRequest?.requestDate ?? assetRequest?.createdAt}
            />
          ),
        }),
      ]}
      detailContent={
        <div className="flex flex-col flex-1 min-h-0 bg-white">
          <RequestDetailsHeader
            variant="entity"
            user={assetRequest?.user}
            status={assetRequest?.status?.toLowerCase()}
            className="rounded-none"
          />

          <div className="flex flex-col gap-8 p-6 flex-1 overflow-auto">
            {!assetRequest ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-sm text-muted-foreground">
                  {t("assets:asset_request_not_found")}
                </p>
              </div>
            ) : (
              <>
                <RequestInfoGrid items={buildAssetInfoItems(assetRequest, t)} />
                <DocumentAttachments
                  items={attachmentUrl ? [{ url: attachmentUrl }] : []}
                  title={t("common:labels.attachments")}
                />
              </>
            )}
          </div>

          {assetRequest && (
            <div className="flex justify-end gap-2 pt-4 px-6 pb-4 border-t mt-auto">
              <Button variant="outline" onClick={() => navigate(-1)}>
                {t("common:buttons.back")}
              </Button>

              {assetRequest.assetCategory === AssetCategories.Consumable ? (
                <PermissionGuard
                  section={PLATFORM_SECTION_ENUM.ASSETS}
                  action="edit"
                  mode="hide"
                >
                  <ActionAcceptConsumableAssetReqDialog
                    requestId={assetRequest.id}
                  >
                    <Button
                      disabled={!isPending}
                      className="bg-success hover:bg-success gap-1.5"
                    >
                      <CircleCheck size={16} />
                      {t("common:buttons.approve")}
                    </Button>
                  </ActionAcceptConsumableAssetReqDialog>
                </PermissionGuard>
              ) : (
                <PermissionGuard
                  section={PLATFORM_SECTION_ENUM.ASSETS}
                  action="edit"
                  mode="hide"
                >
                  <ActionAcceptAssetReqDialog
                    requestId={assetRequest.id}
                    assetTypeId={assetRequest.assetTypeId}
                    oldAssetId={assetRequest.assetId ?? undefined}
                    quantity={assetRequest.requestedQuantity}
                  >
                    <Button
                      disabled={!isPending}
                      className="bg-success hover:bg-success gap-1.5"
                    >
                      <CircleCheck size={16} />
                      {t("common:buttons.approve")}
                    </Button>
                  </ActionAcceptAssetReqDialog>
                </PermissionGuard>
              )}

              <PermissionGuard
                section={PLATFORM_SECTION_ENUM.ASSETS}
                action="edit"
                mode="hide"
              >
                <ActionRejectAssetReqDialog requestId={assetRequest.id}>
                  <Button
                    disabled={!isPending}
                    className="bg-danger hover:bg-danger gap-1.5"
                  >
                    <CircleXIcon size={16} />
                    {t("common:buttons.reject")}
                  </Button>
                </ActionRejectAssetReqDialog>
              </PermissionGuard>
            </div>
          )}
        </div>
      }
    />
  );
};
